import { Controller } from "@hotwired/stimulus"

// The values a Lead Finder filter offers while it is being typed into.
//
// Three kinds of field share this controller.
//
// A strict field - Industry, Seniority, Country, anything the provider will
// only accept from its own list - is picked from: the panel opens on focus with
// the head of the vocabulary, typing filters it, and nothing off the list can
// be committed.
//
// A field that only suggests - Job title, whose list is the 999 most common
// ones rather than every title there is - gets the same panel and keeps every
// way in it had, Enter and paste included.
//
// A field with no list at all asks the same endpoint, is told so, and behaves
// exactly as it always did.
//
// The list itself never travels: Technologies alone is 25,649 entries, so the
// filtering happens on the server and only the top 50 come back.
export default class extends Controller {
    static targets = ['suggestionList', 'fieldValue', 'list']
    static values = {
        fieldName: String,
        suggests: Boolean,
        strict: Boolean,
        includeOnly: Boolean,
        exclusionOnly: Boolean,
        url: { type: String, default: '/lead_finder/search_field' }
    }

    connect() {
        this.id = this.element.dataset.controllerId || crypto.randomUUID()
        this.typingTimeout = null
        // Answers for anything but the latest keystroke are dropped: "sales"
        // fires four requests and they do not come back in order.
        this.generation = 0
        this.results = []
        this.rows = []
        this.total = 0
        this.picked = 0
        this.activeIndex = -1

        this.boundFocus = this.handleFocus.bind(this)
        this.boundGlobalFocus = this.handleGlobalFocus.bind(this)
        this.boundClickOutside = this.handleClickOutside.bind(this)

        document.addEventListener('lead_finder_completions:focus', this.boundGlobalFocus)
        this.fieldValueTarget.addEventListener('focus', this.boundFocus)
        document.addEventListener('click', this.boundClickOutside)

        // The other direction: a chip taken off while the panel is open puts
        // its value back in the list. Only removals, because an addition is
        // this controller's own doing and already re-runs the search.
        if (this.hasListTarget) {
            this.listObserver = new MutationObserver((mutations) => {
                if (!mutations.some((mutation) => mutation.removedNodes.length)) return
                if (!this.isOpen()) return
                this.search(this.fieldValueTarget.value.trim())
            })
            this.listObserver.observe(this.listTarget, { childList: true })
        }
    }

    isOpen() {
        return this.hasSuggestionListTarget && this.suggestionListTarget.innerHTML !== ''
    }

    disconnect() {
        clearTimeout(this.typingTimeout)
        if (this.listObserver) this.listObserver.disconnect()
        this.fieldValueTarget.removeEventListener('focus', this.boundFocus)
        document.removeEventListener('lead_finder_completions:focus', this.boundGlobalFocus)
        document.removeEventListener('click', this.boundClickOutside)
    }

    // One panel open at a time: fourteen filter sections stacked in a column
    // would otherwise leave a trail of them behind.
    handleGlobalFocus(event) {
        if (event.detail.controllerId === this.id) return
        this.close()
    }

    handleClickOutside(event) {
        if (!this.element.contains(event.target)) this.close()
    }

    handleFocus(event) {
        this.element.dispatchEvent(new CustomEvent('lead_finder_completions:focus', {
            bubbles: true,
            detail: { controllerId: this.id }
        }))

        // A field with values to offer opens on focus showing them. One with
        // none has nothing to show until something is typed.
        if (this.suggestsValue || event.target.value.trim().length >= 2) this.onInput(event)
    }

    onInput(event) {
        clearTimeout(this.typingTimeout)

        const typed = (event.target.value || '').trim()
        if (!this.suggestsValue && typed.length < 2) { this.close(); return }

        this.typingTimeout = setTimeout(() => this.search(typed), 200)
    }

    onKeydown(event) {
        if (event.key === 'Escape') { this.close(); return }

        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            if (!this.rows.length) return
            event.preventDefault()
            const step = event.key === 'ArrowDown' ? 1 : -1
            this.activeIndex = (this.activeIndex + step + this.rows.length) % this.rows.length
            this.highlight()
            return
        }

        if (event.key !== 'Enter') return

        // On a strict field Enter is the only way in, so it takes the
        // highlighted row and never the raw text - even when there is no row,
        // where doing nothing is the point. Anywhere else it takes the
        // highlighted row if there is one and otherwise falls through to
        // multi-value-input, which commits what was typed.
        // stopImmediatePropagation, not stopPropagation: multi-value-input's
        // own Enter handler is bound to this same element, and only the
        // immediate form stops a listener that is already a sibling.
        if (this.activeIndex >= 0 && this.rows.length) {
            event.preventDefault()
            event.stopImmediatePropagation()
            this.commit(this.rows[this.activeIndex].key, false)
        } else if (this.strictValue) {
            // Enter in a text input submits the form it sits in, and this one
            // runs the search. Nothing was picked, so nothing should happen.
            event.preventDefault()
            event.stopImmediatePropagation()
        }
    }

    async search(typed) {
        const generation = ++this.generation

        const params = new URLSearchParams({
            field_name: this.fieldNameValue,
            field_value: typed.includes(',') ? typed.split(',').pop() : typed
        })

        try {
            const response = await fetch(`${this.urlValue}?${params.toString()}`, {
                headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
            })
            if (!response.ok) throw new Error('Network error')

            const data = await response.json()
            if (generation !== this.generation) return

            // A field with no list is told so and shows nothing.
            if (!data.has_taxonomy) { this.close(); return }

            // A value already in the field has nothing left to offer. Filtered
            // here rather than asked of the server, because the field can hold
            // two hundred values after a paste and they would not fit in a
            // query string.
            const picked = this.pickedValues()
            this.picked = picked.size
            this.results = (data.results || []).filter((item) => !picked.has(String(item.key).toLowerCase()))
            this.total = Math.max((data.total || 0) - picked.size, this.results.length)

            // On a field that only suggests, what has been typed is a value in
            // its own right and leads the list. It is the whole affordance:
            // nothing else on the row tells you that a list of the 999 most
            // common job titles is not the only thing you may search for.
            const own = typed.trim()
            const offerOwn = !this.strictValue && own.length > 0 &&
                !picked.has(own.toLowerCase()) &&
                !this.results.some((item) => String(item.key).toLowerCase() === own.toLowerCase())

            this.rows = offerOwn ? [{ key: own, own: true }].concat(this.results) : this.results.slice()
            this.activeIndex = this.rows.length ? 0 : -1
            this.render(typed)
        } catch (error) {
            if (generation !== this.generation) return
            // A panel that cannot open leaves the field exactly as it was.
            this.close()
        }
    }

    close() {
        this.results = []
        this.rows = []
        this.activeIndex = -1
        if (this.hasSuggestionListTarget) this.suggestionListTarget.innerHTML = ''
    }

    // Whichever sign they carry: an excluded value is in the filter too, and
    // offering it again is how the same row ends up in the list twice.
    pickedValues() {
        if (!this.hasListTarget) return new Set()

        return new Set(
            Array.from(this.listTarget.querySelectorAll('[data-tag="value"]'))
                .map((tag) => (tag.dataset.value || '').trim().toLowerCase())
                .filter(Boolean)
        )
    }

    // Both ways in go through multi-value-input#addValue, which owns the chips
    // and the hidden inputs; this only decides which value and which sign.
    //
    // It calls that controller rather than planting a button carrying
    // `click->multi-value-input#addValue` and clicking it: Stimulus binds
    // actions through a MutationObserver, whose callback runs after the current
    // task, so a button appended and clicked in the same tick is still an
    // ordinary button when the click lands. Nothing happened, on every pick.
    commit(value, exclusion) {
        const input = this.application.getControllerForElementAndIdentifier(this.element, 'multi-value-input')
        if (!input) return

        input.addValue({
            type: 'click',
            currentTarget: {
                dataset: {
                    value: value,
                    multiValueInputExclusionValue: String(exclusion),
                    multiValueInputSkipEnterCheckValue: 'true',
                    // A taxonomy value is one value, commas and all: "Retail,
                    // Wholesale" is an industry, not two.
                    multiValueInputSkipSplittingValue: 'true'
                }
            }
        })

        // The panel counts what is applied by listening for `input` and
        // `change` on itself, and a click on a suggestion is neither. Without
        // this the chip lands and "Search leads" stays greyed out, because
        // `searchable` never moves off zero.
        document.dispatchEvent(new CustomEvent('values-changed', { bubbles: true }))

        this.fieldValueTarget.value = ''
        this.fieldValueTarget.focus()
        this.search('')
    }

    highlight() {
        const rows = this.suggestionListTarget.querySelectorAll('[data-suggest-row]')
        rows.forEach((row, index) => row.classList.toggle('is-active', index === this.activeIndex))
        const active = rows[this.activeIndex]
        if (active) active.scrollIntoView({ block: 'nearest' })
    }

    render(typed) {
        this.suggestionListTarget.innerHTML = ''

        if (!this.rows.length) {
            const empty = document.createElement('div')
            empty.className = 'lf-suggest lf-suggest-empty'
            if (typed) {
                empty.textContent = `No accepted value matches "${typed}"`
            } else if (this.picked) {
                // Not "nothing to pick from": there was, and it is all in the
                // field already.
                empty.textContent = this.strictValue
                    ? 'Every accepted value is already in this filter'
                    : 'Every suggestion is already in this filter - type your own'
            } else {
                empty.textContent = 'Nothing to pick from'
            }
            this.suggestionListTarget.appendChild(empty)
            return
        }

        const panel = document.createElement('div')
        panel.className = 'lf-suggest'

        const list = document.createElement('div')
        list.className = 'lf-suggest-list'

        this.rows.forEach((item, index) => {
            const row = document.createElement('div')
            row.className = 'lf-suggest-row' + (item.own ? ' lf-suggest-row--own' : '') +
                            (index === this.activeIndex ? ' is-active' : '')
            row.setAttribute('data-suggest-row', '')
            row.addEventListener('mouseenter', () => { this.activeIndex = index; this.highlight() })

            const label = document.createElement('span')
            label.className = 'lf-suggest-label'
            label.textContent = item.key
            label.title = item.key

            if (item.own) {
                const own = document.createElement('span')
                own.className = 'lf-suggest-own'
                own.textContent = 'your value'
                label.appendChild(own)
            }

            const actions = document.createElement('div')
            actions.className = 'lf-suggest-actions'

            if (!this.exclusionOnlyValue) {
                const include = this.actionButton('include', 'Add as an included value',
                    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><polyline points="20 6 9 17 4 12"/></svg>')
                include.addEventListener('click', () => this.commit(item.key, false))
                actions.appendChild(include)
            }

            if (!this.includeOnlyValue) {
                const exclude = this.actionButton('exclude', 'Add as an excluded value',
                    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><path stroke-linecap="round" stroke-linejoin="round" d="M5 12h14"/></svg>')
                exclude.addEventListener('click', () => this.commit(item.key, true))
                actions.appendChild(exclude)
            }

            // Clicking the row itself includes: the two buttons are for the
            // choice, not for the common case.
            row.addEventListener('click', (event) => {
                if (event.target.closest('.lf-field-action')) return
                this.commit(item.key, this.exclusionOnlyValue)
            })

            row.appendChild(label)
            row.appendChild(actions)
            list.appendChild(row)
        })

        panel.appendChild(list)

        const foot = []
        // A field that only suggests says so, every time it is open. Its list
        // is the most common values, not the permitted ones, and someone who
        // does not know that will not type past it.
        if (!this.strictValue) foot.push('Suggestions only - type any value you like')
        // Fifty of 25,649 is a different sentence from fifty of fifty.
        if (this.total > this.results.length) {
            foot.push(`${this.results.length} of ${this.total.toLocaleString()} shown, keep typing to narrow it down`)
        }

        if (foot.length) {
            const more = document.createElement('p')
            more.className = 'lf-suggest-more'
            more.textContent = foot.join(' · ')
            panel.appendChild(more)
        }

        this.suggestionListTarget.appendChild(panel)
    }

    actionButton(kind, title, svg) {
        const button = document.createElement('button')
        button.type = 'button'
        button.className = `lf-field-action lf-field-action--${kind}`
        button.title = title
        button.innerHTML = svg
        return button
    }
}
